import { useState, useEffect } from 'react';
import { navLinks } from '@/mocks/homeData';

interface NavbarProps {
  onOpenQuote: () => void;
}

export default function Navbar({ onOpenQuote }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (href: string) => {
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      {/* Mobile Logo Banner */}
      <div className="md:hidden relative z-40 bg-background-950 pt-20 pb-6 px-4">
        <a
          href="#hero"
          onClick={(e) => {
            e.preventDefault();
            handleNavClick('#hero');
          }}
          className="block w-full"
        >
          <img
            src="https://storage.readdy-site.link/project_files/da605b4b-e525-4d78-92ac-a53f525d58ea/945392c7-1aaf-4a12-ae66-157c92416ec4_compressed_photo_2026-07-19_10-56-20.webp"
            alt="Avenor Meridian - Global Supply, Engineered Excellence"
            className="w-full h-auto object-contain"
          />
        </a>
      </div>

      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'bg-background-950/95 backdrop-blur-md border-b border-background-800/50'
            : 'bg-transparent md:bg-transparent bg-background-950/80'
        }`}
      >
        <div className="section-padding flex items-center justify-between h-16 md:h-32">
          {/* Logo — Desktop only */}
          <a
            href="#hero"
            onClick={(e) => {
              e.preventDefault();
              handleNavClick('#hero');
            }}
            className="hidden md:flex items-center shrink-0"
          >
            <img
              src="https://storage.readdy-site.link/project_files/da605b4b-e525-4d78-92ac-a53f525d58ea/945392c7-1aaf-4a12-ae66-157c92416ec4_compressed_photo_2026-07-19_10-56-20.webp"
              alt="Avenor Meridian - Global Supply, Engineered Excellence"
              className="h-28 w-auto object-contain"
            />
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(link.href);
                }}
                className="px-4 py-2 text-sm font-medium text-foreground-300 hover:text-primary-400 transition-colors duration-300 whitespace-nowrap tracking-wide uppercase"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Desktop CTA */}
          <button
            onClick={() => {
              setMobileOpen(false);
              onOpenQuote();
            }}
            className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 border border-primary-500 text-primary-400 text-sm font-semibold rounded-md hover:bg-primary-500 hover:text-background-950 transition-all duration-300 whitespace-nowrap tracking-wide uppercase"
          >
            Request a Quote
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-xs"></i>
            </span>
          </button>

          {/* Mobile: CTA + Hamburger */}
          <div className="md:hidden flex items-center justify-between w-full">
            <button
              onClick={() => {
                setMobileOpen(false);
                onOpenQuote();
              }}
              className="px-3 py-2 text-xs font-semibold text-background-950 bg-primary-500 rounded-md hover:bg-primary-400 transition-colors duration-300 whitespace-nowrap tracking-wide uppercase"
            >
              Request a Quote
            </button>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="w-10 h-10 flex items-center justify-center text-foreground-200"
              aria-label="Toggle menu"
            >
              <span className="w-6 h-6 flex items-center justify-center">
                <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`}></i>
              </span>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-500 ${
            mobileOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className="bg-background-950/98 backdrop-blur-lg border-t border-background-800/50 px-4 py-4 space-y-1">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(link.href);
                }}
                className="block px-4 py-3 text-sm font-medium text-foreground-300 hover:text-primary-400 hover:bg-background-900/50 rounded-md transition-colors duration-300 whitespace-nowrap tracking-wide uppercase"
              >
                {link.label}
              </a>
            ))}
            <button
              onClick={() => {
                setMobileOpen(false);
                onOpenQuote();
              }}
              className="block w-full mt-3 px-4 py-3 text-center text-sm font-semibold text-background-950 bg-primary-500 rounded-md hover:bg-primary-400 transition-colors duration-300 whitespace-nowrap tracking-wide uppercase"
            >
              Request a Quote
            </button>
          </div>
        </div>
      </nav>
    </>
  );
}